import { ajax, AjaxError } from 'rxjs/ajax';
import { from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { UnknownProp } from 'MyTypes';
import { composeApiUrl } from './compose-api-url';
import { IAuthorizationDto } from 'redux/session/session';
import { getCurrentUser, renewCurrentUser } from 'services/current-user/current-user.service';

type HeaderPropTypes = {
  [key: string]: any;
};

type XMLHttpRequestResponseType = '' | 'arraybuffer' | 'blob' | 'document' | 'json' | 'text';

const getClientId = (): string => {
  const storedClientId = sessionStorage.getItem('clientId');

  if (storedClientId) {
    return storedClientId;
  }

  const newClientId = `${Math.random().toString(36).substring(2, 11)}-${Date.now().toString(36)}`;
  sessionStorage.setItem('clientId', newClientId);

  return newClientId;
};

const clientId = getClientId();

const defaultHeaders: HeaderPropTypes = { 'Content-Type': 'application/json', 'X-ClientId': clientId };

const composeHeaders = (headers: HeaderPropTypes) => {
  delete defaultHeaders.Authorization;

  return {
    ...defaultHeaders,
    ...headers,
  };
};

const buildFormData = (formData: FormData, data: UnknownProp, parentKey?: string) => {
  if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File) && !(data instanceof Blob)) {
    Object.keys(data).forEach((key) => {
      buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
    });
  } else {
    const value = data == null ? ('' as string) : (data as Blob);
    formData.append(parentKey as string, value);
  }
};

export const makeImageUpload = <T>(url: string, blob: Blob, postData: Record<string, T>, signal: AbortSignal) => {
  const form = new FormData();

  form.append('file', blob);
  buildFormData(form, postData);

  const requestUrl = composeApiUrl(url);

  const performUpload = () => fetch(requestUrl, {
    method: 'POST',
    body: form,
    signal,
    credentials: 'include',
    headers: { 'X-ClientId': clientId },
  });

  // A concurrent session renewal can invalidate the cookie this upload was sent with
  // (see session-renewal-remediation.md). Retry once, waiting for any in-flight renewal first.
  return refreshToken()
    .then(performUpload)
    .then((response) => (response.status === 401 ? refreshToken().then(performUpload) : response));
};

let onTokenStored: Promise<void> | null = null;
const refreshToken = () => {
  const currentUser = getCurrentUser();
  // currentUser is persisted in sessionStorage, so it survives a tab reload — a missing value here
  // reliably means "never authenticated", not "state lost from memory while the cookie is still live".
  if (currentUser && !shouldRenewToken(currentUser.sessionExpiresAt)) return Promise.resolve(null);

  // renew request should be dispatched only the first time token expiration is detected
  // following requests will wait for the renewed token
  if (!onTokenStored) {
    onTokenStored = fetch(composeApiUrl('session/renew'), {
      method: 'POST',
      headers: composeHeaders({}),
      body: JSON.stringify({}),
      credentials: 'include'
    })
      .then(async (response: Response) => {
        if (!response.ok) throw new Error(`session/renew failed with status ${response.status}`);
        const session: IAuthorizationDto = await response.json();
        renewCurrentUser(session);
      })
      .finally(() => {
        // Reset regardless of outcome: on success a later request may need a fresh renewal,
        // and on failure this unblocks everything waiting on this promise instead of wedging forever.
        onTokenStored = null;
      });
  }

  // returning this Promise will make all requests wait for the renewed token
  return onTokenStored;
};

const shouldRenewToken = (expiresAt: string): boolean => {
  const nowSec = new Date().getTime() / 1000;
  const expiresAtSec = new Date(expiresAt).getTime() / 1000;
  const diff = expiresAtSec - nowSec;
  return diff < 60;
};

const makeRequest = (
  requestFunc: string,
  url: string,
  composeHeadersFunc: Function,
  body: Object | null,
  responseType: XMLHttpRequestResponseType = 'json'
) => {
  const performRequest = () => {
    const requestUrl = composeApiUrl(url);
    const requestHeaders = composeHeadersFunc({});

    return ajax({
      url: requestUrl,
      responseType,
      method: requestFunc,
      headers: requestHeaders,
      body,
      withCredentials: true,
    });
  };

  // Session bootstrap/teardown calls must never trigger a renewal attempt: there's no session
  // yet to renew before the initial `session/{urlId}` call, and no point renewing before logout.
  if (url.startsWith('session/')) return performRequest();

  return from(refreshToken())
    .pipe(
      switchMap(performRequest),
      catchError((error: AjaxError) => {
        if (error?.status !== 401) return throwError(() => error);

        // A concurrent session renewal can invalidate the cookie this request was sent with
        // (see session-renewal-remediation.md). Wait for any in-flight renewal, then retry once.
        // If the retry also 401s, this error propagates and existing global 401 handling applies.
        return from(refreshToken()).pipe(switchMap(performRequest));
      })
    );
};

export const makeGet = (url: string) => makeRequest('GET', url, composeHeaders, null);

export const makeGetFile = (url: string) => makeRequest('GET', url, composeHeaders, null, 'blob');

export const makePost = (url: string, body: Object = {}) => makeRequest('POST', url, composeHeaders, body);

export const makePut = (url: string, body: Object = {}) => makeRequest('PUT', url, composeHeaders, body);

export const makePatch = (url: string, body: Object = {}) => makeRequest('PATCH', url, composeHeaders, body);
